import { captureLazySentryException } from '@/app/providers/sentry/lazy'

type WalletCleanupEntry = {
  id: string
  order: number
  cleanup: () => void | Promise<void>
}

type WalletCleanupError = {
  id: string
  error: unknown
}

export const WALLET_CLEANUP_ORDER = {
  auth: 0,
  app: 5,
  rfqClientGateway: 7,
  sharedWallet: 8,
  subaccount: 10,
  exchange: 20,
  account: 30,
  activity: 40,
  position: 50,
  referral: 60,
  authZ: 70,
  access: 80,
  notification: 90,
  wallet: 100
} as const

export const WALLET_CLEANUP_IDS = {
  auth: 'auth:logout',
  app: 'app:reset',
  rfqClientGateway: 'rfqClientGateway:disconnect',
  sharedWallet: 'sharedWallet:logout',
  subaccount: 'subaccount:reset',
  exchange: 'exchange:reset',
  account: 'account:reset',
  activity: 'activity:reset',
  position: 'position:reset',
  referral: 'referral:reset',
  authZ: 'authZ:reset',
  access: 'access:reset',
  notification: 'notification:closeAnnouncements',
  wallet: 'wallet:resetAutoSignSetup'
} as const

export const REQUIRED_WALLET_CLEANUP_IDS = Object.values(WALLET_CLEANUP_IDS)

const walletCleanupRegistry = new Map<string, WalletCleanupEntry>()

export const registerWalletCleanup = (entry: WalletCleanupEntry) => {
  walletCleanupRegistry.set(entry.id, entry)
}

export const resetWalletCleanupRegistry = () => {
  walletCleanupRegistry.clear()
}

export const getRegisteredWalletCleanupIds = () => {
  return [...walletCleanupRegistry.keys()]
}

export const assertRequiredWalletCleanupsRegistered = () => {
  const missingCleanupIds = REQUIRED_WALLET_CLEANUP_IDS.filter((cleanupId) => {
    return !walletCleanupRegistry.has(cleanupId)
  })

  if (missingCleanupIds.length === 0) {
    return
  }

  throw new Error(
    `Missing wallet cleanup registrations: ${missingCleanupIds.join(', ')}`
  )
}

export const runWalletCleanup = async () => {
  const errors: WalletCleanupError[] = []
  const entries = [...walletCleanupRegistry.values()].sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order
    }

    return left.id.localeCompare(right.id)
  })

  for (const entry of entries) {
    try {
      await entry.cleanup()
    } catch (error) {
      errors.push({ error, id: entry.id })
      captureLazySentryException(error, {
        message: `[WalletCleanup:${entry.id}] Cleanup failed`,
        attributes: {
          feature: 'wallet',
          operation: 'cleanup',
          walletCleanupId: entry.id
        }
      })
    }
  }

  return errors
}
